import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Archive, RotateCcw, RefreshCw, Loader2, Search, AlertTriangle,
  ChevronRight, ChevronDown, Eye, FileText, Inbox,
} from 'lucide-react';

const ENTITY_LABELS = {
  customer:       { ar: 'العملاء',        en: 'Customers',       icon: '👤' },
  supplier:       { ar: 'الموردون',       en: 'Suppliers',       icon: '🏭' },
  project:        { ar: 'المشاريع',       en: 'Projects',        icon: '🏗️' },
  invoice:        { ar: 'الفواتير',       en: 'Invoices',        icon: '🧾' },
  payment:        { ar: 'المدفوعات',      en: 'Payments',        icon: '💵' },
  inventory:      { ar: 'المخزون',        en: 'Inventory',       icon: '📦' },
  employee:       { ar: 'الموظفون',       en: 'Employees',       icon: '👷' },
  cost_center:    { ar: 'مراكز التكلفة',  en: 'Cost Centers',    icon: '🎯' },
  subcontractor:  { ar: 'مقاولو الباطن',  en: 'Subcontractors',  icon: '👷‍♂️' },
  subcontract:    { ar: 'عقود الباطن',    en: 'Subcontracts',    icon: '📋' },
  quotation:      { ar: 'عروض الأسعار',   en: 'Quotations',      icon: '📝' },
  credit_note:    { ar: 'إشعارات دائنة',  en: 'Credit Notes',    icon: '↩️' },
  purchase_order: { ar: 'أوامر الشراء',   en: 'Purchase Orders', icon: '🛒' },
  supplier_bill:  { ar: 'فواتير الموردين', en: 'Supplier Bills', icon: '📥' },
  asset:          { ar: 'الأصول',         en: 'Assets',          icon: '🏢' },
  budget:         { ar: 'الموازنات',      en: 'Budgets',         icon: '📊' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const displayNameFor = (entityType, row) => {
  if (!row) return '—';
  return row.name_english || row.name_arabic || row.name || row.full_name
       || row.invoice_number || row.credit_note_number || row.quotation_no
       || row.po_number || row.bill_number || row.payment_no || row.code
       || row.item_name || row.id?.slice(0, 8);
};

export default function ArchiveCenter({ user }) {
  const [activeTab, setActiveTab] = useState('center');         // center | events
  const [activeEntity, setActiveEntity] = useState('customer');
  const [totals, setTotals] = useState({});
  const [rowsByEntity, setRowsByEntity] = useState({});
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [dependents, setDependents] = useState({});

  const loadArchiveCenter = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/archive/center');
      setRowsByEntity(data.by_entity || {});
      setTotals(data.totals || {});
    } catch (err) {
      toast.error('فشل تحميل الأرشيف: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/archive/events?limit=200');
      setEvents(data.events || []);
    } catch (err) {
      toast.error('فشل تحميل سجل الأحداث: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (activeTab === 'center') { loadArchiveCenter(); } }, [activeTab, loadArchiveCenter]);
  useEffect(() => { if (activeTab === 'events') { loadEvents(); } }, [activeTab, loadEvents]);

  const handleRestore = async (entityType, row) => {
    const name = displayNameFor(entityType, row);
    if (!window.confirm(`استرجاع "${name}" من الأرشيف؟`)) return;
    setRestoringId(row.id);
    try {
      await axiosInstance.post(`/archive/${entityType}/${row.id}/restore`);
      toast.success(`تم استرجاع: ${name}`);
      await loadArchiveCenter();
    } catch (err) {
      toast.error('فشل الاسترجاع: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setRestoringId(null);
    }
  };

  const handleViewDependents = async (entityType, row) => {
    if (expandedRow === row.id) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(row.id);
    if (!dependents[row.id]) {
      try {
        const { data } = await axiosInstance.get(`/archive/${entityType}/${row.id}/dependents`);
        setDependents(prev => ({ ...prev, [row.id]: data }));
      } catch (err) {
        toast.error('فشل قراءة العلاقات: ' + (err?.response?.data?.detail || err.message));
      }
    }
  };

  const currentRows = rowsByEntity[activeEntity] || [];
  const filteredRows = useMemo(() => {
    if (!search) return currentRows;
    const q = search.toLowerCase();
    return currentRows.filter(r => JSON.stringify(r).toLowerCase().includes(q));
  }, [currentRows, search]);

  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="archive-center-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Archive className="w-7 h-7 text-amber-600" />
              مركز الأرشيف · Archive Center
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              السجلات المؤرشفة (Soft-Deleted) — يمكن استرجاعها في أي وقت بدون فقدان البيانات.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-xl border border-slate-200 px-4 py-2 shadow-sm">
              <div className="text-xs text-slate-500">الإجمالي</div>
              <div className="text-2xl font-bold text-amber-600">{grandTotal}</div>
            </div>
            <button
              onClick={activeTab === 'center' ? loadArchiveCenter : loadEvents}
              disabled={loading}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="archive-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              تحديث
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mt-4 border-b border-slate-200">
          {[
            { id: 'center', label: 'السجلات المؤرشفة', icon: Inbox },
            { id: 'events', label: 'سجل العمليات', icon: FileText },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                activeTab === t.id
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
              data-testid={`archive-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'center' && (
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-4">
          {/* Entity sidebar */}
          <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm h-fit">
            <div className="text-xs font-semibold text-slate-500 mb-2 px-2">نوع السجل</div>
            <div className="space-y-1">
              {Object.keys(ENTITY_LABELS).map(et => {
                const count = totals[et] || 0;
                const active = activeEntity === et;
                return (
                  <button
                    key={et}
                    onClick={() => { setActiveEntity(et); setExpandedRow(null); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${
                      active ? 'bg-amber-50 text-amber-700 font-semibold' : 'hover:bg-slate-50 text-slate-700'
                    }`}
                    data-testid={`archive-entity-${et}`}
                  >
                    <span className="flex items-center gap-2">
                      <span>{ENTITY_LABELS[et].icon}</span>
                      <span>{ENTITY_LABELS[et].ar}</span>
                    </span>
                    {count > 0 && (
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        active ? 'bg-amber-200 text-amber-900' : 'bg-slate-100 text-slate-600'
                      }`}>{count}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span>{ENTITY_LABELS[activeEntity]?.icon}</span>
                {ENTITY_LABELS[activeEntity]?.ar} المؤرشفة ({filteredRows.length})
              </h2>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="بحث..."
                  className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-64"
                  data-testid="archive-search"
                />
              </div>
            </div>

            {loading ? (
              <div className="p-12 text-center text-slate-500">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                جاري التحميل...
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Archive className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">لا توجد سجلات مؤرشفة في هذا القسم</p>
                <p className="text-sm mt-1">جميع البيانات هنا نشطة</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3 text-right font-semibold">السجل</th>
                      <th className="px-4 py-3 text-right font-semibold">تاريخ الأرشفة</th>
                      <th className="px-4 py-3 text-right font-semibold">بواسطة</th>
                      <th className="px-4 py-3 text-right font-semibold">السبب</th>
                      <th className="px-4 py-3 text-right font-semibold">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredRows.map(row => (
                      <React.Fragment key={row.id}>
                        <tr className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium text-slate-900">
                            {displayNameFor(activeEntity, row)}
                            <div className="text-xs text-slate-400 font-mono">{row.id?.slice(0, 8)}…</div>
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{formatDate(row.archived_at)}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{row.archived_by_name || row.archived_by || '—'}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs italic">{row.archive_reason || '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewDependents(activeEntity, row)}
                                className="p-1.5 hover:bg-slate-100 rounded text-slate-600 hover:text-slate-900"
                                title="عرض العلاقات"
                                data-testid={`archive-view-deps-${row.id}`}
                              >
                                {expandedRow === row.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => handleRestore(activeEntity, row)}
                                disabled={restoringId === row.id}
                                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium flex items-center gap-1 disabled:opacity-50"
                                data-testid={`archive-restore-${row.id}`}
                              >
                                {restoringId === row.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-3 h-3" />
                                )}
                                استرجاع
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedRow === row.id && dependents[row.id] && (
                          <tr className="bg-amber-50">
                            <td colSpan="5" className="px-6 py-3 text-xs">
                              <div className="flex items-start gap-2 text-amber-900">
                                <AlertTriangle className="w-4 h-4 mt-0.5" />
                                <div className="flex-1">
                                  <div className="font-semibold mb-2">
                                    العلاقات الحالية ({dependents[row.id].total} سجل مرتبط):
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {dependents[row.id].details.map((d, i) => (
                                      <span
                                        key={i}
                                        className={`px-2 py-1 rounded-md text-xs ${
                                          d.count > 0 ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-600'
                                        }`}
                                      >
                                        {d.label}: {d.count}
                                      </span>
                                    ))}
                                  </div>
                                  {dependents[row.id].has_dependents && (
                                    <p className="mt-2 text-amber-800">
                                      استرجاع هذا السجل سيُعيد ظهوره في القوائم النشطة فقط — السجلات المرتبطة لم تتأثر.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'events' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">سجل عمليات الأرشفة والاسترجاع</h2>
            <p className="text-xs text-slate-500 mt-1">آخر 200 عملية في الشركة الحالية</p>
          </div>
          {loading ? (
            <div className="p-12 text-center text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin mx-auto" />
            </div>
          ) : events.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>لا توجد عمليات مسجلة بعد</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-right font-semibold">الوقت</th>
                    <th className="px-4 py-3 text-right font-semibold">العملية</th>
                    <th className="px-4 py-3 text-right font-semibold">النوع</th>
                    <th className="px-4 py-3 text-right font-semibold">المعرّف</th>
                    <th className="px-4 py-3 text-right font-semibold">المستخدم</th>
                    <th className="px-4 py-3 text-right font-semibold">السبب</th>
                    <th className="px-4 py-3 text-right font-semibold">العلاقات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {events.map((ev, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-xs text-slate-600">{formatDate(ev.ts)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                          ev.action === 'archive'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}>
                          {ev.action === 'archive' ? 'أرشفة' : 'استرجاع'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-700">
                        {ENTITY_LABELS[ev.entity_type]?.ar || ev.entity_type}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-slate-500">{ev.entity_id?.slice(0, 8)}…</td>
                      <td className="px-4 py-3 text-xs text-slate-700">{ev.user_name || ev.user_id || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-600 italic">{ev.reason || '—'}</td>
                      <td className="px-4 py-3 text-xs">
                        {ev.dependents_snapshot?.total > 0 ? (
                          <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                            {ev.dependents_snapshot.total} مرتبط
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
